import {
    EventPublic,
    ServicePublic,
    SolutionPublic,
} from '@yandex-data-ui/cloud-schemas/build/models/ui-api';
import {Author} from 'components/AuthorBlock/AuthorBlock';
import {BackLinkProps} from 'components/BackLink/BackLink';
import {Lang} from 'contexts/LangContext';
import {
    ButtonProps,
    CardBaseProps,
    HeaderOffset,
    HeaderWidth,
    ImageProps,
    MediaVideoProps,
    Animatable,
    MediaProps,
    BlockHeaderProps,
    Themable,
    CardData,
    BlockBaseProps,
    PageContent,
    Block as PageConstructorBlock,
} from '@yandex-data-ui/page-constructor';
import {NavigationItemExtended, ServiceData} from 'models/app';
import {HeaderBreadCrumbsProps} from 'units/constructor-local/components/HeaderBreadcrumbs/HeaderBreadcrumbs';

export enum BlockType {
    CalculatorBlock = 'calculator-block',
    ServiceDemoBlock = 'service-demo-block',
    HeaderBlock = 'header-block',
    ServicesBlock = 'services-block',
    FormBlock = 'form-block',
    Quotes = 'quotes',
    Card = 'card',
    Quote = 'quote',
    Event = 'event',
    Post = 'post',
    ServiceCard = 'service-card',
    ServicePreviewCard = 'service-preview-card',
    Form = 'form',
}

export enum DataSource {
    Blog = 'blog',
    Events = 'events',
    Services = 'services',
}

export enum MetaLocale {
    Ru = 'ru_RU',
    En = 'en_EN',
}

export type HeaderImageSize = 's' | 'm';
export type ServicesBlockSize = 's' | 'm';
export type HeaderBackLink = Pick<BackLinkProps, 'url' | 'title'>;
export type NavigationItemBlockSize = 's' | 'm';

interface HeaderBackgorundProps {
    fullWidth?: boolean;
    color?: string;
    url?: string;
    disableCompress?: boolean;
}

export type HeaderBlockBackground = HeaderBackgorundProps | MediaProps;

export interface HeaderBlockProps {
    title: string;
    overtitle?: string;
    description?: string;
    buttons?: Pick<ButtonProps, 'url' | 'text' | 'theme' | 'primary'>[];
    width?: HeaderWidth;
    imageSize?: HeaderImageSize;
    offset?: HeaderOffset;
    image?: ImageProps;
    video?: MediaVideoProps;
    backLink?: HeaderBackLink;
    breadcrumbs?: HeaderBreadCrumbsProps;
    background?: HeaderBlockBackground;
    theme?: 'default' | 'dark';
    verticalOffset: 's' | 'm' | 'l' | 'xl';
}

export type ServiceDemoProps = Animatable;
export type CalculatorProps = Animatable;

export interface ServicesBlockProps extends BlockHeaderProps {
    size?: ServicesBlockSize;
}

export interface FormBlockProps {
    slug: string;
}

export interface ServiceCardItem {
    name: string;
    id: string;
    url: string;
}

export interface ServiceCardProps extends CardBaseProps {
    title: string;
    description: string;
    services: ServiceCardItem[];
    link?: {
        text: string;
        url: string;
    };
}

export type ServicePreviewCardProps = Pick<
    ServicePublic,
    'slug' | 'name' | 'description' | 'tag' | 'status' | 'docUrl' | 'pricesUrl'
> &
    CardBaseProps;

export type ServiceCard = {
    type: BlockType.ServiceCard;
} & ServiceCardProps;

export type ServicePreviewCard = {
    type: BlockType.ServicePreviewCard;
} & ServicePreviewCardProps;

export interface FormProps {
    id: number | string;
    metrikaGoals: string[];
    border?: boolean;
}

export type FormBlock = {
    type: BlockType.FormBlock;
} & FormBlockProps;

export type Form = {
    type: BlockType.Form;
} & FormProps;

export interface QuoteItem extends Themable {
    text: string;
    image: string;
    logo: string;
    color?: string;
    url?: string;
    author?: Author;
}

export type QuotesProps = {
    items: QuoteItem[];
};

export interface CardProps extends CardBaseProps, CardData {}

export type Quotes = {
    type: BlockType.Quotes;
} & QuotesProps;

export type Card = {
    type: BlockType.Card;
} & CardProps;

export interface EventProps extends EventPublic, CardBaseProps {
    className?: string;
    fullSize?: boolean;
    lang?: Lang;
}

export interface QuoteProps extends Themable, CardBaseProps {
    text: string;
    image: ImageProps;
    logo: string;
    color?: string;
    url?: string;
    author?: Author;
}

export interface PostProps extends PostData, CardBaseProps {}

export type Quote = {
    type: BlockType.Quote;
} & QuoteProps;

export type Event = {
    type: BlockType.Event;
} & EventProps;

export type Post = {
    type: BlockType.Post;
} & PostProps;

export type CalculatorBlock = {
    type: BlockType.CalculatorBlock;
} & CalculatorProps;

export type ServiceDemoBlock = {
    type: BlockType.ServiceDemoBlock;
} & ServiceDemoProps;

export type HeaderBlock = {
    type: BlockType.HeaderBlock;
} & HeaderBlockProps;

export type ServicesBlock = {
    type: BlockType.ServicesBlock;
} & ServicesBlockProps;

export type BlockV1 = Form | Quotes | Card | Quote | Event | Post | ServiceCard;

type BlockV2Raw = ServiceDemoBlock | CalculatorBlock | HeaderBlock | FormBlock | ServicesBlock;

export type BlockV2 = BlockV2Raw & BlockBaseProps;
export type Block = BlockV1 | BlockV2 | PageConstructorBlock;

//TODO: get it from cloud-schemas CLOUDFRONT-8475
export interface PostData {
    id: number;
    slug: string;
    title: string;
    date: string;
    url: string;
}

export type LoadableItem = EventPublic | PostData | ServicePublic;

export const BlockV2Types = Object.values(BlockType);

//page models

export interface Menu {
    title: string;
}

export type PageService = Pick<
    ServicePublic,
    'id' | 'name' | 'slug' | 'status' | 'consoleUrl' | 'icon'
>;
export type PageSolution = Pick<SolutionPublic, 'id' | 'name' | 'description' | 'image'>;

export interface BasePageData {
    name: string;
    title: string;
    metaDescription: string;
    keywords: string[];
    noIndex?: boolean;
    shareTitle?: string;
    shareDescription?: string;
    shareImage?: string;
    service?: PageService;
    solution?: PageSolution;
}

export interface PageData extends BasePageData {
    content: PageContent;
}

export type LoadableData = LoadableItem | LoadableItem[] | undefined;

export function headerHasMediaBackground(
    background: HeaderBackgorundProps | MediaProps,
): background is MediaProps {
    return 'image' in background || 'video' in background || 'youtube' in background;
}

export function isServiceNavigationItem(item: NavigationItemExtended): item is ServiceData {
    return 'docUrl' in item;
}
